import {
	selectBylineConnectionIcons,
	type BylineConnectionIconSource,
	type BylineConnectionIconSources,
} from './getBylineConnectionIcons.ts';
import { isFirstPartyProduct } from './getProductAppIcon.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

// ORS resolves any public website through this catch-all provider; the result is a site, not an app.
const GENERIC_WEBSITE_PROVIDER_KEY = 'public-object-provider';

export type BylineLinkIdentityCounts = {
	total: number;
	firstParty: number;
	thirdParty: number;
};

export type BylineOutgoingLinkIdentityCounts = BylineLinkIdentityCounts & {
	generic: number;
	unresolved: number;
};

export type BylineIconCountAttributes = {
	ssrPlaceholderCount: number;
	/** Distinct real app icons the resolved links produce, capped at three, independent of the SSR reservation. */
	clientIconCount: number;
	isSsrLoad: boolean;
	iconSources: BylineConnectionIconSource[];
	iconsBySource: Record<BylineConnectionIconSource, number>;
	linksTo: BylineOutgoingLinkIdentityCounts;
	appearsIn: BylineLinkIdentityCounts;
	relatedContent: BylineLinkIdentityCounts;
	overlapCount: number;
};

type LinkIdentity = 'firstParty' | 'thirdParty' | 'generic' | 'unresolved';

// First-party means the stack draws an AtlasKit app logo for it. Generic and unresolved only occur
// for outgoing links: graph rows arrive with an entity type, so ORS always has a provider for them.
const classifyLink = (link: EnhancedSmartLink): LinkIdentity => {
	if (isFirstPartyProduct(link.product)) {
		return 'firstParty';
	}
	if (link.key === GENERIC_WEBSITE_PROVIDER_KEY) {
		return 'generic';
	}
	if (!link.product && !link.key) {
		return 'unresolved';
	}
	return 'thirdParty';
};

const countOutgoingLinks = (
	links: readonly EnhancedSmartLink[],
): BylineOutgoingLinkIdentityCounts => {
	const counts: BylineOutgoingLinkIdentityCounts = {
		total: links.length,
		firstParty: 0,
		thirdParty: 0,
		generic: 0,
		unresolved: 0,
	};
	for (const link of links) {
		counts[classifyLink(link)] += 1;
	}
	return counts;
};

const countGraphLinks = (links: readonly EnhancedSmartLink[]): BylineLinkIdentityCounts => {
	const firstParty = links.filter((link) => isFirstPartyProduct(link.product)).length;
	return { total: links.length, firstParty, thirdParty: links.length - firstParty };
};

/**
 * The payload compares the tiles captured on the page's first render with up to three eligible app icons.
 * Source counts describe that selection before SSR truncation and fallback padding. Link identity
 * counts describe each source. Only counts and section names are included, never link data.
 */
export const getBylineIconCountAttributes = ({
	sources,
	ssrPlaceholderCount,
	isSsrLoad,
}: {
	sources: BylineConnectionIconSources;
	ssrPlaceholderCount: number;
	isSsrLoad: boolean;
}): BylineIconCountAttributes => {
	const { icons, overlapCount } = selectBylineConnectionIcons(sources);
	const iconsBySource: Record<BylineConnectionIconSource, number> = {
		devDocs: 0,
		relatedContent: 0,
		appearsIn: 0,
		linksTo: 0,
	};
	const iconSources: BylineConnectionIconSource[] = [];
	for (const { source } of icons) {
		iconSources.push(source);
		iconsBySource[source] += 1;
	}

	return {
		ssrPlaceholderCount,
		clientIconCount: icons.length,
		isSsrLoad,
		iconSources,
		iconsBySource,
		linksTo: countOutgoingLinks(sources.outgoingLinks ?? []),
		appearsIn: countGraphLinks(sources.incomingLinks ?? []),
		relatedContent: countGraphLinks(sources.relatedContentLinks ?? []),
		overlapCount,
	};
};
